// Entry (D-33, which replaces UI-S1; UI-C1, UI-C2; D-03, D-18, D-30, EM-2, EM-6, EM-18).
//
// ONE screen, in the owner's own order: new or existing at the top, the code only for existing, the name,
// "I speak" and "I read" as dropdowns that are always on the screen, who is on the phone, and one button
// that says what it will do. Nothing is behind a link and there is no second menu.
//
// The reason is a session that is already spent: the owner's seat joined speaking English because the old
// screen defaulted from his iPhone's language list and put "I speak" inside a summary line behind a
// "Change" link. He spoke Arabic into an English model for the whole majlis and believed he was testing his
// own. The two dropdowns below are rendered unconditionally - no flag, no expander, no sheet - and
// entry.test.tsx asserts both are in the document after a mount with no interaction at all.
//
// MAJLIS-42 (D-33, the approved mock-up's frames 1b-1e): the VALUE of "I speak" is the person's own, never the
// phone's. With nothing remembered it opens on an empty first row ("Choose…") with one line saying why it matters,
// and Create / Join asks for it instead of guessing (the button stays enabled, like the empty-name case); a
// remembered value says it is remembered. "I read in" keeps its device default (consultant ruling 9).
//
// MAJLIS-126 (D-55, UI-C35; mock-up frames (a)-(c)): Your rooms and Listed rooms sit ABOVE the form (RoomsList.tsx).
// Tapping a listed room puts it on the form as a card: Listed with key shows "Existing majlis" pressed and the code
// field EMPTY and focused, with "Ask {host} for the code" under it; Listed open shows no mode switch and no code field.
// A Your rooms row rejoins with this form's remembered name, languages and phone kind, by the room id.
//
// MAJLIS-137 (D-57, UI-C38; the owner-approved passive-mode mock-up, frames (a)/(b)): a THIRD option in the mode switch,
// "Passive". Choosing it shows its one-line subtitle under the switch (today's hint style), then ONLY "I read in" and a
// "Start" button: the code, the name, "I speak" and "This phone" are hidden - a passive room has no code, its seat is the
// room's (shared, Arabic-primary), and its name is this form's remembered name, or "Passive" when there is none.
import { useEffect, useMemo, useRef, useState, type FormEvent, type RefObject } from "react";
import { languageName, pickerOrder } from "../data/languages";
import { READ_LANGUAGES } from "../data/readLanguages";
import { SPEAK_LANGUAGES, type Language } from "../data/speakLanguages";
import { useI18n, type StringKey } from "../i18n";
import { shown as shownKey } from "../i18n/pending";
import type { Failure } from "../protocol/client";
import { cleanCode, CODE_LEN, normalizeCode } from "../protocol/roomCode";
import type { ListedRoom } from "../protocol/rooms";
import type { SeatKind } from "../protocol/types";
import type { YourRoom } from "../room/yourRooms";
import { ChevronDownIcon, ChevronEndIcon } from "./icons";
import { ChosenRoom, RoomSections, withName } from "./RoomsList";

export interface EntryChoice {
  code: string | null; // null = create a new majlis (unless `listing` names a listed room)
  /** MAJLIS-126 (P-36): the listing handle of the room chosen in Listed rooms (with `code` for a keyed one) */
  listing?: string;
  name: string;
  speak: string;
  read: string;
  seatKind: SeatKind;
  /** MAJLIS-137 (P-38): start a PASSIVE room (code null; `speak` and `seatKind` are the room's, not asked for) */
  passive?: boolean;
}

export type EntryMode = "new" | "existing" | "passive";

/** MAJLIS-171: each segment's full words (its accessible name) and the short words it draws (Passive is one word, so
 * both). A short key still pending its verdict would draw the full words (i18n/pending.ts). */
export const SEGMENT_WORDS: Record<EntryMode, { full: StringKey; short: StringKey }> = {
  new: { full: "modeNew", short: "modeNewShort" },
  existing: { full: "modeExisting", short: "modeExistingShort" },
  passive: { full: "modePassive", short: "modePassiveShort" }, // its own short word (ru «Слушать»; elsewhere = modePassive)
};

export const FAILURE_TEXT: Record<Failure | "offline", StringKey> = {
  room_not_found: "errNotFound",
  room_closed: "errClosed",
  room_full: "errFull",
  rate_limited: "errRateLimited",
  replaced: "errReplaced",
  server_error: "errServer",
  offline: "errOffline",
  removed: "errRemoved", // MAJLIS-80 (ruling e)
  denied: "errDenied", // MAJLIS-37 (P-26): the mock-up's 2b title
  knock_timeout: "errKnockTimeout", // MAJLIS-37 (P-26): the mock-up's 4d title
};

export function EntryScreen({
  hostLink = false,
  linkCode,
  initialName,
  speak,
  speakRemembered = false,
  read,
  seatKind,
  onSpeak,
  onRead,
  onSeatKind,
  busy,
  failure,
  onSubmit,
  records = 0,
  onMyMajlis,
  yours = [],
  listed = [],
  onRejoin,
  now = 0,
}: {
  /** MAJLIS-126 (UI-C35): this phone's rooms (Your rooms) and the listed rooms; absent: the sections are not shown. */
  yours?: readonly YourRoom[];
  listed?: readonly ListedRoom[] | null;
  /** MAJLIS-126 (P-36): a Your rooms row, with this form's name, languages and phone kind. */
  onRejoin?: (roomId: string, choice: Omit<EntryChoice, "code">) => void;
  /** MAJLIS-145: the app's injected wall clock at this render - Your rooms' "Today" / "Yesterday" */
  now?: number;
  /** MAJLIS-67 (UI-S3, mock-up 1b): how many finished majlis this device's identity hosts. The "My majlis" row is shown
   * ONLY when there is at least one; a first-time visitor sees this screen unchanged. */
  records?: number;
  onMyMajlis?: () => void;
  /** MAJLIS-80 (UI-C14): this page was opened by a host link. The join names its room by the link's secret, so the
   * screen asks for no code and offers no "new majlis": only the name, the languages and the phone. */
  hostLink?: boolean;
  linkCode: string | null;
  initialName: string;
  /** "" = nothing chosen yet (MAJLIS-42): the dropdown shows its empty first row and the form asks for it. */
  speak: string;
  /** MAJLIS-42 (1d): the value shown is one this phone remembered, not one just picked here. */
  speakRemembered?: boolean;
  read: string;
  seatKind: SeatKind;
  onSpeak: (tag: string) => void;
  onRead: (tag: string) => void;
  onSeatKind: (kind: SeatKind) => void;
  busy: "join" | "create" | null;
  failure: Failure | "offline" | null;
  onSubmit: (choice: EntryChoice) => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(initialName);
  const [problem, setProblem] = useState<"errName" | "errSpeak" | null>(null);
  const speakRef = useRef<HTMLSelectElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  // MAJLIS-126 (mock-up (c)): the listed room chosen from the list, shown as a card at the top of the form
  const [chosen, setChosen] = useState<ListedRoom | null>(null);

  // Mode and code are DERIVED from the invite link, with whatever the person has typed layered on top and
  // tagged with the link it was typed against:
  //   - no link, nothing typed  -> New majlis, empty code (D-33 point 12)
  //   - a link, nothing typed   -> Existing, the code filled in (D-33 point 8, EM-18)
  //   - a link that ARRIVES later (a hashchange) -> the tag stops matching, so the new link wins again
  // Derivation rather than an effect: there is no frame in which the link is present and the screen still
  // says New, and nothing is remounted, so a half-typed name survives the link arriving.
  const [edited, setEdited] = useState<{ link: string | null; mode: EntryMode; code: string } | null>(null);
  const live = edited && edited.link === linkCode ? edited : null;
  const mode: EntryMode = live?.mode ?? (linkCode ? "existing" : "new");
  const code = live?.code ?? linkCode ?? "";
  const setMode = (next: EntryMode) => setEdited({ link: linkCode, mode: next, code });
  const setCode = (next: string) => setEdited({ link: linkCode, mode, code: next });

  const keyed = chosen?.kind === "key";
  const passive = mode === "passive" && !hostLink && !chosen; // MAJLIS-137
  const complete =
    hostLink || passive || (chosen !== null && !keyed) || (!chosen && mode === "new") || normalizeCode(code) !== null;
  // MAJLIS-126: a keyed room's code field is focused the moment the room is chosen (EMPTY: the list carries no code)
  useEffect(() => {
    if (keyed) codeRef.current?.focus();
  }, [keyed, chosen]);
  const choose = (room: ListedRoom) => {
    setChosen(room);
    setProblem(null);
    if (room.kind === "key") setEdited({ link: linkCode, mode: "existing", code: "" });
  };

  /** The name and "I speak" are asked for before anything is sent (the same checks as the button's). */
  const ready = (): boolean => {
    if (!name.trim()) {
      setProblem("errName");
      return false;
    }
    if (!speak) {
      setProblem("errSpeak");
      speakRef.current?.focus();
      return false;
    }
    return true;
  };
  // Long enough to judge and still not a code: say so, rather than leaving a dead button unexplained.
  const badCode = !hostLink && mode === "existing" && code.length >= CODE_LEN && normalizeCode(code) === null;

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (passive) {
      // MAJLIS-137: nothing to ask - the reading language is always set; the name is the remembered one, or "Passive"
      setProblem(null);
      onSubmit({ code: null, passive: true, name: name.trim() || t("modePassive"), speak, read, seatKind: "shared" });
      return;
    }
    if (!name.trim()) return setProblem("errName");
    if (!speak) {
      // MAJLIS-42 (1c): nothing is sent; the dropdown is marked and focused, the error is the form's role=alert line
      setProblem("errSpeak");
      speakRef.current?.focus();
      return;
    }
    if (!complete) return; // the button is disabled; this is the keyboard's way in
    setProblem(null);
    if (chosen) {
      // MAJLIS-126 (P-36): the chosen listed room by its handle - with the typed code when it is Listed with key
      onSubmit({ code: keyed ? code : null, listing: chosen.handle, name: name.trim(), speak, read, seatKind });
      return;
    }
    // a host link names no code: App joins by the link's secret (MAJLIS-80)
    onSubmit({ code: hostLink || mode === "new" ? null : code, name: name.trim(), speak, read, seatKind });
  };

  const shown = problem ? t(problem) : badCode ? t("errCode") : failure ? t(FAILURE_TEXT[failure]) : null;
  const label =
    busy === "join"
      ? t("joining")
      : busy === "create"
        ? t("creating")
        : passive
          ? t("passiveStart")
          : mode === "new" && !hostLink && !chosen
            ? t("createMajlis")
            : t("joinMajlis");
  const sections = !hostLink && !chosen && listed !== null && onRejoin !== undefined;

  return (
    <main className="entry" data-spec="UI-S1 D-33">
      <h1 className="brand">
        <span lang="ar">مجلس</span> <span lang="en">Majlis</span>
      </h1>
      <p className="tagline">{t("tagline")}</p>
      {records > 0 && onMyMajlis && (
        <button
          type="button"
          className="my-majlis-row"
          aria-label={t("myMajlisCount", { label: t("myMajlis"), n: records })}
          onClick={onMyMajlis}
          data-spec="UI-S3 UI-C13 D-16 P-20"
        >
          <span className="my-majlis-text">
            <span className="my-majlis-title">{t("myMajlis")}</span>
            <span className="hint">{t("myMajlisHint")}</span>
          </span>
          <span className="my-majlis-count">{records}</span>
          <ChevronEndIcon />
        </button>
      )}
      {sections && (
        <RoomSections
          yours={yours}
          listed={listed ?? []}
          onRejoin={(roomId) => {
            if (!ready()) return;
            setProblem(null);
            onRejoin?.(roomId, { name: name.trim(), speak, read, seatKind });
          }}
          now={now}
          onChoose={choose}
        />
      )}
      <form className="entry-card" data-spec="UI-C1 P-1" onSubmit={submit} noValidate>
        {chosen && (
          <ChosenRoom
            room={chosen}
            onClear={() => {
              setChosen(null);
              setEdited(null);
              setProblem(null);
            }}
          />
        )}
        {chosen && !keyed ? null : hostLink ? (
          <p className="hint host-link-entry" data-spec="UI-C14 D-15 EM-18">
            {t("hostLinkEntry")}
          </p>
        ) : (
          // MAJLIS-171 (component-rules.md section 6; the screen-rules mock-up, frames (f) and (g)): three EQUAL segments on
          // ONE row, each with its SHORT words (New / Existing / Passive: no "majlis") at every width - the consultant's
          // ruling 1 of 2026-10-05, one look in every language. The accessible name is always the full words.
            <div className="mode-switch" role="group" aria-label={t("modeGroup")} data-spec="D-33 UI-C1">
              {(["new", "existing", "passive"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  id={`mode-${m}`}
                  className="mode-option"
                  aria-pressed={mode === m}
                  aria-label={t(SEGMENT_WORDS[m].full)}
                  onClick={() => {
                    if (chosen && m === "new") setChosen(null); // a new majlis is not the chosen room
                    setMode(m);
                    setProblem(null);
                  }}
                >
                  <span className="seg-word" aria-hidden="true">
                    {t(shownKey(SEGMENT_WORDS[m].short) ?? SEGMENT_WORDS[m].full)}
                  </span>
                </button>
              ))}
            </div>
        )}
        {passive && (
          <p className="hint passive-hint" data-spec="UI-C38 D-57">
            {t("passiveHint")}
          </p>
        )}

        {mode === "existing" && !hostLink && (!chosen || keyed) && (
          <div className="field" data-spec="D-03 D-30 EM-2 EM-18">
            <label htmlFor="room-code">{t("roomCode")}</label>
            <input
              id="room-code"
              ref={codeRef}
              className="text-input code-input"
              dir="ltr"
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              aria-describedby="room-code-hint"
              value={code}
              // Exactly what the server's normalize_code forgives, through the one shared transform: a
              // pasted "7k 3m" or "7K-3M" becomes 7K3M as it lands (roomCode.test.ts holds the two in step).
              onChange={(e) => setCode(cleanCode(e.target.value))}
            />
            <span id="room-code-hint" className="hint">
              {chosen ? withName(t, "askCode", chosen.host_name) : t("roomCodeHint")}
            </span>
          </div>
        )}

        {!passive && (
        <div className="field">
          <label htmlFor="name">{t("name")}</label>
          <input
            id="name"
            className="text-input"
            dir="auto"
            autoComplete="nickname"
            maxLength={40}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-spec="D-03"
          />
        </div>
        )}

        {passive ? (
          <LanguageSelect id="read" label={t("read")} list={READ_LANGUAGES} value={read} onChange={onRead} spec="UI-C2 UI-C38 D-06" />
        ) : (
        <div className="pair">
          <LanguageSelect
            id="speak"
            label={t("speak")}
            list={SPEAK_LANGUAGES}
            value={speak}
            onChange={(tag) => {
              onSpeak(tag);
              if (problem === "errSpeak") setProblem(null);
            }}
            spec="UI-C2 D-04 D-21 D-33 MAJLIS-42"
            placeholder={t("speakChoose")}
            hint={speak && speakRemembered ? t("speakRemembered") : t("speakHint")}
            invalid={problem === "errSpeak"}
            selectRef={speakRef}
          />
          <LanguageSelect id="read" label={t("read")} list={READ_LANGUAGES} value={read} onChange={onRead} spec="UI-C2 D-06 D-21 D-33" />
        </div>
        )}

        {!passive && (
        <fieldset className="field seat-kind" data-spec="UI-C2 D-18 EM-6">
          <legend className="label">{t("thisPhone")}</legend>
          <div className="kind-row">
            {(["personal", "shared"] as const).map((k) => (
              <label key={k} className="choice">
                <input type="radio" name="seat-kind" value={k} checked={seatKind === k} onChange={() => onSeatKind(k)} />
                <span>{k === "personal" ? t("seatPersonal") : t("seatShared")}</span>
              </label>
            ))}
          </div>
        </fieldset>
        )}

        {shown && (
          <p className="form-error" role="alert">
            {shown}
          </p>
        )}
        <button type="submit" className="primary" disabled={busy !== null || !complete} data-spec="UI-C1 UI-P2 P-1 D-33">
          {label}
        </button>
      </form>
    </main>
  );
}

/** One always-visible dropdown over a real language list, labelled and ordered like the picker (UI-C2). */
function LanguageSelect({
  id,
  label,
  list,
  value,
  onChange,
  spec,
  placeholder,
  hint,
  invalid = false,
  selectRef,
}: {
  id: string;
  label: string;
  list: readonly Language[];
  value: string;
  onChange: (tag: string) => void;
  spec: string;
  /** MAJLIS-42: the empty first row while nothing is chosen (value ""); absent: the list has no empty row. */
  placeholder?: string;
  /** MAJLIS-42: one line under the dropdown, which is also its accessible description. */
  hint?: string;
  invalid?: boolean;
  selectRef?: RefObject<HTMLSelectElement>;
}) {
  const { lang } = useI18n();
  const rows = useMemo(() => pickerOrder(list, lang), [list, lang]);
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="select-wrap">
        <select
          id={id}
          ref={selectRef}
          className="select"
          value={value}
          data-spec={spec}
          aria-describedby={hint ? `${id}-hint` : undefined}
          aria-invalid={invalid || undefined}
          required={placeholder !== undefined}
          onChange={(e) => onChange(e.target.value)}
        >
          {placeholder !== undefined && value === "" && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {rows.map((l) => (
            <option key={l.tag} value={l.tag}>
              {languageName(l.tag, lang)}
            </option>
          ))}
        </select>
        <span className="select-chevron" aria-hidden="true">
          <ChevronDownIcon />
        </span>
      </div>
      {hint && (
        <span id={`${id}-hint`} className="hint">
          {hint}
        </span>
      )}
    </div>
  );
}
